import { ITEM_GROUP } from 'containers/RouteRestriction'
import {
  CROSS_SINGLE_AVERAGE_PRICE_REVENUE_QUERY,
  SOLITARY_SINGLE_SEGMENTATION_QUERY,
} from 'query/query'
import { Options } from 'hooks/useSegmentLeaderboardKPIs'

export enum QueryType {
  PercentOfMarket,
  PercentOfSegment,
  WeightedUnits,
  AveragePrice,
}

interface ColumnConfig {
  queryDocument: Options['queryDocument']
  dataAccessor: Options['dataAccessor']
}

const queryTypeConfig: Record<QueryType, ColumnConfig> = {
  [QueryType.PercentOfMarket]: {
    queryDocument: SOLITARY_SINGLE_SEGMENTATION_QUERY,
    dataAccessor: 'marketShare',
  },
  [QueryType.PercentOfSegment]: {
    queryDocument: SOLITARY_SINGLE_SEGMENTATION_QUERY,
    dataAccessor: 'percent',
  },
  [QueryType.WeightedUnits]: {
    queryDocument: SOLITARY_SINGLE_SEGMENTATION_QUERY,
    dataAccessor: 'weighted',
  },
  [QueryType.AveragePrice]: {
    queryDocument: CROSS_SINGLE_AVERAGE_PRICE_REVENUE_QUERY,
    dataAccessor: 'average',
  },
}

export type KPIColumn = Options & {
  label: string
  restrictions?: ITEM_GROUP[]
}

export const kpiColumn = (
  label: string,
  queryType: QueryType,
  {
    crossVar,
    filters = [],
    hintText,
  }: { crossVar?: string; filters?: Options['filters']; hintText?: Options['hintText'] } = {},
  restrictions?: ITEM_GROUP[]
): KPIColumn => ({
  ...queryTypeConfig[queryType],
  crossVar,
  filters,
  label,
  hintText,
  restrictions,
})

export const kpiColumns: Tuple<KPIColumn, 13> = [
  kpiColumn('Market Share', QueryType.PercentOfMarket),
  kpiColumn('No. of Units', QueryType.WeightedUnits),
  kpiColumn('Traditional shopper', QueryType.PercentOfSegment, {
    filters: [
      { variable_id: 27, answer_code: 3 }, // Advertising on social media
      { variable_id: 27, answer_code: 4 }, // Advertising online
    ],
    hintText: 'Proportion of Shoppers buying in-store.',
  }),
  kpiColumn('Digital shopper', QueryType.PercentOfSegment, {
    filters: [
      { variable_id: 27, answer_code: 1 }, // Advertising on TV
      { variable_id: 27, answer_code: 2 }, // Advertising in magazine / newspaper
    ],
    hintText: 'Proportion of Shoppers buying online.',
  }),
  kpiColumn('Digital ad trigger', QueryType.PercentOfSegment, {
    hintText: 'Proportion saying social media or online advertising inspired them to buy.',
    filters: [
      { variable_id: 691, answer_code: 1 }, // Yes
    ],
  }),
  kpiColumn('In-store trigger', QueryType.PercentOfSegment, {
    hintText:
      'Proportion saying in-store staff, leaflets or display advertising inspired them to buy.',
    filters: [
      { variable_id: 692, answer_code: 1 }, // Yes
    ],
  }),
  kpiColumn('Price paid', QueryType.AveragePrice, {
    crossVar: 'globalPurchasePriceMidPoint',
    hintText: 'Average price paid (€).',
  }),
  kpiColumn(
    'Price paid (Mobile)',
    QueryType.AveragePrice,
    {
      crossVar: 'mobileHandsetNSPMidPoint',
      hintText: 'Average price paid (€).',
    },
    [ITEM_GROUP.MOBILE_PHONE]
  ),
  kpiColumn('High price sensitivity', QueryType.PercentOfSegment, {
    filters: [
      { variable_id: 689, answer_code: 2 },
      { variable_id: 689, answer_code: 3 },
      { variable_id: 689, answer_code: 2920 },
    ],
    hintText:
      "Price sensitivity is a typology derived from answers given across the survey. A scoring system is developed from mentions of 'Price' and 'Promotions' at key brand and retailer choice drivers questions as well as 'Price comparison site' usage. The more mentions of these, the higher the sensitivity score.",
  }),
  kpiColumn('Retailers visited (2 or more)', QueryType.PercentOfSegment, {
    filters: [
      { variable_id: 695, answer_code: 2 }, // 2
      { variable_id: 695, answer_code: 3 }, // 3 or more
    ],
    hintText: 'Number of retailers visited in-store / online as part of purchase journey.',
  }),
  kpiColumn('Brands considered (3 or more)', QueryType.PercentOfSegment, {
    filters: [
      { variable_id: 683, answer_code: 3 }, // 3
      { variable_id: 683, answer_code: 4 }, // 4
      { variable_id: 683, answer_code: 2919 }, // 5 or more
    ],
    hintText: 'Number of brands considered during purchase journey.',
  }),
  kpiColumn('Brand is key', QueryType.PercentOfSegment, {
    hintText: "Proportion mentioning 'Product brand' in their top 3 choice drivers.",
    filters: [
      { variable_id: 697, answer_code: 1 }, // Yes
    ],
  }),
  kpiColumn('Features are key', QueryType.PercentOfSegment, {
    hintText: "Proportion mentioning 'Product features' in their top 3 choice drivers.",
    filters: [{ variable_id: 698, answer_code: 1 }], // Yes
  }),
]

export const kpiTotalBase = kpiColumn('Total base', QueryType.PercentOfMarket)

export const productColumns = ['Price paid', 'Price paid (Mobile)']
